/**
 * Cliente de API para o chat de suporte público.
 * Rota pública — não requer token de autenticação.
 */

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatRequest {
  message: string
  history: ChatMessage[]
  userEmail?: string
  sessionId?: string
}

export interface UserContext {
  email: string
  name?: string
  role: string
  projectCount: number
  hasPro: boolean
}

export interface ChatResponse {
  reply: string
  requiresEmail: boolean
  userContext?: UserContext
  suggestedReplies: string[]
}

export interface RelatedArticle {
  id: string
  title: string
  sourceUrl: string | null
}

export interface PlanStep {
  id: string
  description: string
  agent?: string
  status: 'pending' | 'running' | 'done' | 'skipped'
}

export interface EvidenceItem {
  sourceType: string
  title?: string
  snippet?: string
  url?: string
}

export interface ToolCallEvent {
  tool: string
  status: 'running' | 'done' | 'failed'
  resultSummary?: string
}

export interface TicketInfo {
  id: string
  number: string
  summary: string
  status: string
}

export interface InteractionOption {
  id: string
  label: string
  description?: string
  metadata?: Record<string, unknown>
}

export interface InteractionEvent {
  type: 'interaction'
  interaction_id: string
  interaction_type: 'single_select' | 'confirm' | 'text_input'
  title: string
  message?: string
  options?: InteractionOption[]
  confirm_action?: string
  confirm_details?: Record<string, unknown>
  placeholder?: string
  context_data?: Record<string, unknown>
}

export type SSEEvent =
  | { type: 'chunk'; text: string }
  | { type: 'thinking'; step: string; text: string; status: 'running' | 'done' }
  | { type: 'requires_email' }
  | { type: 'done'; suggestedReplies: string[]; userContext?: UserContext; relatedArticles?: RelatedArticle[]; evidence?: EvidenceItem[] }
  | { type: 'error'; message: string }
  | { type: 'plan'; steps: PlanStep[] }
  | { type: 'tool_call'; tool: string; status: 'running' | 'done' | 'failed'; resultSummary?: string }
  | { type: 'evidence'; sources: EvidenceItem[] }
  | { type: 'confirm'; action: string; details: Record<string, unknown>; runStepId: string }
  | { type: 'clarify'; question: string; options?: string[] | null }
  | { type: 'ticket_created'; ticket: TicketInfo }
  | InteractionEvent

export async function streamSupportMessage(
  req: ChatRequest,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/support/chat/stream', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
    signal,
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Erro no streaming de suporte: ${res.status} ${text}`)
  }

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          onEvent(JSON.parse(line.slice(6)) as SSEEvent)
        } catch {
          // ignora linhas malformadas
        }
      }
    }
  }
}

// ── KB Query (artigos only, RAG) ──────────────────────────────────────────

export interface KBQueryRequest {
  query: string
}

export interface KBArticleRef {
  id: string
  title: string
  sourceUrl: string | null
}

export interface KBQueryResponse {
  answer: string
  hasAnswer: boolean
  articles: KBArticleRef[]
}

export async function queryKnowledgeBase(query: string): Promise<KBQueryResponse> {
  const res = await fetch('/api/support/kb/query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Erro na consulta KB: ${res.status} ${text}`)
  }

  return res.json()
}

export async function fetchChatHistory(sessionId: string): Promise<ChatMessage[]> {
  const res = await fetch(`/api/support/chat/history?session_id=${encodeURIComponent(sessionId)}`)
  if (!res.ok) return []
  const data = await res.json()
  return (data.messages ?? []) as ChatMessage[]
}

export async function sendSupportMessage(req: ChatRequest): Promise<ChatResponse> {
  const res = await fetch('/api/support/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Erro no chat de suporte: ${res.status} ${text}`)
  }

  return res.json()
}
